import * as React from "react";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { searchSerial, searchSerialList } from "../api/saleOutwardApi";
import { MAX_SERIALS_PER_OUTWARD } from "../config/saleKinds";
import type { SaleOutwardAction, SaleOutwardState } from "../state/saleOutwardReducer";
import { buildOrderLineView, findOrderLineForRow, wouldExceedAfterAdding } from "../state/selectors";
import type { SaleOrderLine, ScannedSerialRow } from "../types";
import { normalizeSerial, scannedSerialSetOf } from "../utils/serial";

/** Lần thêm serial thành công gần nhất, hiện ở dòng trạng thái cạnh ô quét */
export interface LastAddedSerial {
  serial: string;
  itemName: string | null;
  /** Dòng đơn cùng mã hàng sau khi thêm, ví dụ "dòng đơn còn thiếu 1"; null khi không xác định */
  remainText: string | null;
}

/** Mô tả dòng đơn cùng mã hàng của serial vừa thêm (rows đã gồm serial đó) */
const describeRemain = (
  orderLines: SaleOrderLine[],
  rows: ScannedSerialRow[],
  row: ScannedSerialRow,
): string | null => {
  const line = findOrderLineForRow(orderLines, row);
  if (!line) return null;
  const [view] = buildOrderLineView([line], rows);
  const missing = (view.primaryQuantity ?? 0) - view.scannedQuantity;
  if (missing > 0) return `dòng đơn còn thiếu ${missing}`;
  if (missing < 0) return `dòng đơn thừa ${-missing}`;
  return "đủ số lượng";
};

/**
 * Quét serial vào phiếu.
 *
 * Sửa các lỗi của trang cũ:
 * - Quét nhanh hai lần cùng lúc: serial đang chờ phản hồi được giữ trong ref (cập nhật đồng bộ),
 *   nên lần quét thứ hai bị chặn ngay, không phụ thuộc state của lần render trước.
 * - Kiểm tra vượt số lượng dựa trên state mới nhất (ref), không phải bản cũ trong closure.
 * - Quét lỗi thì serial không bị giữ lại, quét lại được.
 * `lastAdded` là lần thêm thành công gần nhất, để trang hiện "Đã thêm ..." cạnh ô quét.
 */
export const useSerialScanner = (
  state: SaleOutwardState,
  dispatch: React.Dispatch<SaleOutwardAction>,
) => {
  const stateRef = React.useRef(state);
  stateRef.current = state;
  const pendingRef = React.useRef(new Set<string>());
  const [scanning, setScanning] = React.useState(false);
  const [lastAdded, setLastAdded] = React.useState<LastAddedSerial | null>(null);

  const scan = React.useCallback(
    async (rawSerial: string, positionId?: number) => {
      const serial = normalizeSerial(rawSerial);
      const { header, rows } = stateRef.current;
      if (!serial) return;
      if (!header.sourceCode) {
        NotificationExtension.Fails("Chưa có số chứng từ");
        return;
      }
      if (scannedSerialSetOf(rows).has(serial) || pendingRef.current.has(serial)) {
        NotificationExtension.Fails(`Mã serial ${serial} đã được quét!`);
        return;
      }
      if (rows.length >= MAX_SERIALS_PER_OUTWARD) {
        NotificationExtension.Warn(`Số lượng serial vượt quá ${MAX_SERIALS_PER_OUTWARD}, vui lòng kiểm tra lại`);
        return;
      }

      pendingRef.current.add(serial);
      setScanning(true);
      try {
        const res = await searchSerial({
          serial,
          sourceCode: header.sourceCode,
          type: header.sourceType ?? "",
          invId: header.fromInvId,
          positionId,
        });
        if (!res?.success || !res.data) {
          NotificationExtension.Fails(res?.message || `Không tìm thấy serial ${serial}`);
          return;
        }
        if (res.data.warning) {
          NotificationExtension.Fails(res.data.warning);
          return;
        }
        const row: ScannedSerialRow = {
          ...res.data,
          invName: res.data.invName,
          note: null,
          primaryQuantity: 1,
          locatorId: res.data.positionId,
        };
        const latest = stateRef.current;
        if (wouldExceedAfterAdding(latest.orderLines, latest.rows, [row])) {
          NotificationExtension.Warn("Sản phẩm vượt quá số lượng cần xuất, vui lòng kiểm tra lại");
          return;
        }
        dispatch({ type: "ADD_ROWS", rows: [row] });
        setLastAdded({
          serial: row.serial || serial,
          itemName: row.itemName ?? null,
          remainText: describeRemain(latest.orderLines, [...latest.rows, row], row),
        });
      } catch (error) {
        console.error("[SaleOutward] quét serial lỗi", error);
        NotificationExtension.Fails(`Quét serial ${serial} lỗi, vui lòng thử lại.`);
      } finally {
        pendingRef.current.delete(serial);
        setScanning(pendingRef.current.size > 0);
      }
    },
    [dispatch],
  );

  /** Thêm nhiều serial một lần (import Excel), gửi một request */
  const addMany = React.useCallback(
    async (rawSerials: string[]) => {
      const { header } = stateRef.current;
      const serials = Array.from(new Set(rawSerials.map(normalizeSerial).filter(Boolean)));
      if (!serials.length) {
        NotificationExtension.Fails("Không tìm thấy serial trong tệp Excel.");
        return;
      }
      if (serials.length > MAX_SERIALS_PER_OUTWARD) {
        NotificationExtension.Warn(`Số lượng serial vượt quá ${MAX_SERIALS_PER_OUTWARD}, vui lòng kiểm tra lại`);
        return;
      }
      if (!header.sourceCode) {
        NotificationExtension.Fails("Chưa có số chứng từ");
        return;
      }
      setScanning(true);
      try {
        const res = await searchSerialList({
          sourceCode: header.sourceCode,
          type: header.sourceType ?? "",
          listSerials: serials,
        });
        if (!res?.success || !Array.isArray(res.data)) {
          NotificationExtension.Fails(res?.message || "Không kiểm tra được danh sách serial.");
          return;
        }
        const latest = stateRef.current;
        const known = scannedSerialSetOf(latest.rows);
        const newRows = res.data
          .filter((row) => row.serial && !known.has(normalizeSerial(row.serial)))
          .map((row): ScannedSerialRow => ({ ...row, primaryQuantity: 1, locatorId: row.positionId }));
        if (wouldExceedAfterAdding(latest.orderLines, latest.rows, newRows)) {
          NotificationExtension.Warn("Sản phẩm vượt quá số lượng cần xuất, vui lòng kiểm tra lại");
          return;
        }
        dispatch({ type: "ADD_ROWS", rows: newRows });
        if (newRows.length) setLastAdded({ serial: `${newRows.length} serial`, itemName: null, remainText: null });
        NotificationExtension.Success(`Đã thêm ${newRows.length} serial từ tệp Excel.`);
      } catch (error) {
        console.error("[SaleOutward] import serial lỗi", error);
        NotificationExtension.Fails("Import serial lỗi, vui lòng thử lại.");
      } finally {
        setScanning(false);
      }
    },
    [dispatch],
  );

  return { scan, addMany, scanning, lastAdded };
};
